import { API_BASE_URL } from "@kupikupi/brand";
import { useQuery } from "@tanstack/react-query";
import { type CaptchaTokenProvider, withCaptchaRetry } from "../captcha-retry";
import { queryProductDetail } from "../endpoints/product";
import { useBackendLang } from "../lang-context";
import type { ProductDetailInput } from "../path-adapter";

interface CaptchaProviderOpts {
  /** Provider that resolves a Yandex SmartCaptcha token when the backend
   * returns ApiError("4601"). Detail is rate-limited by SearchAbuseGuard.
   * `| undefined` so consumers can forward `useCaptchaTokenProvider()`
   * directly under exactOptionalPropertyTypes. */
  captchaTokenProvider?: CaptchaTokenProvider | undefined;
  /**
   * Whether the caller currently has a JWT to attach. Folded into the query
   * key (as a boolean only — never the token itself) so the SSR anonymous
   * prefetch cache (favorite:false) is NEVER reused by a logged-in client
   * that needs the per-user fields. Pass `auth.ready ? !!auth.token : undefined`;
   * while ready is false, leave undefined so the hook doesn't fire — the SSR
   * cache covers the paint, and the resolved authPresent triggers exactly one
   * fresh fetch.
   */
  authPresent?: boolean;
}

/**
 * Fetch full SPU detail (title / price / SKU / image list / spec attrs /
 * activity / coupon flags / showQc / favorite). Backend: `GET /product/detail`.
 *
 * The hook must run on the client — Yandex SmartCaptcha is a browser widget,
 * so server components can't recover from 4601.
 *
 * `authPresent` participates in the queryKey: the SSR prefetch (cookie-less,
 * therefore anonymous) keys off `authPresent=false`, while a logged-in client
 * keys off `authPresent=true` and fires a fresh request that carries the JWT
 * via apiFetch's auto-attach. The two responses are never conflated, which
 * fixes the "logged-in user's PDP shows favorite:false forever" bug.
 */
export function useProductDetail(
  input: { channel: ProductDetailInput["channel"]; spuNo: string } | null,
  opts: CaptchaProviderOpts = {},
) {
  const lang = useBackendLang();
  const authPresent = opts.authPresent;
  return useQuery({
    queryKey: ["product", "detail", input?.channel, input?.spuNo, lang, !!authPresent],
    queryFn: () => {
      if (!input) throw new Error("input required");
      return withCaptchaRetry(
        (captchaToken) =>
          queryProductDetail(
            { baseUrl: API_BASE_URL },
            { channel: input.channel, spuNo: input.spuNo },
            captchaToken === undefined ? { lang } : { captchaToken, lang },
          ),
        opts.captchaTokenProvider,
      );
    },
    // Wait for the caller to commit to an authPresent value — while it's
    // undefined the auth bootstrap is still in flight; the SSR-prefetched data
    // covers the paint. Once the boolean is supplied, fire: true for logged-in
    // (per-user fields), false for genuinely anonymous (SSR + logged-out share
    // the same cache).
    enabled: !!input?.channel && !!input?.spuNo && authPresent !== undefined,
    staleTime: 60 * 1000,
  });
}
